import React, { useState, useMemo, useEffect } from "react";
import {
  User, Phone, Heart, ShieldCheck, FileText, Sparkles,
  ChevronLeft, ChevronRight, CheckCircle2, Loader2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { endpoints } from "../lib/api";
import { toast } from "sonner";

/**
 * Multi-step onboarding modal - converts a `ready_to_onboard` member into an
 * `membership_active` one with full personal/emergency/health/preference data.
 *
 * Design: 4 short steps with grouped fields. Sticky checklist + progress
 * footer. Anything not required is clearly optional.
 */
const STEPS = [
  { key: "basic",      label: "Basics",         icon: User },
  { key: "emergency",  label: "Emergency",      icon: Heart },
  { key: "membership", label: "Membership",     icon: ShieldCheck },
  { key: "extras",     label: "Waiver & goals", icon: Sparkles },
];

// Required fields per step (used for completion checklist)
const REQUIRED = {
  basic: ["name", "phone", "dob"],
  emergency: ["emergency_contact_name", "emergency_contact_phone"],
  membership: ["plan", "start_date", "expiry_date"],
  extras: [], // waiver+goals optional
};

function isFilled(form, key) {
  const v = form[key];
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  return true;
}

function stepCompletion(form, stepKey) {
  const req = REQUIRED[stepKey] || [];
  if (req.length === 0) return true;
  return req.every(k => isFilled(form, k));
}

export default function OnboardingModal({ member, open, onClose, onChanged }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!member) return;
    setForm({
      name: member.name || "",
      phone: member.phone || "",
      email: member.email || "",
      dob: member.dob ? new Date(member.dob).toISOString().slice(0, 10) : "",
      gender: member.gender || "",
      address: member.address || "",
      emergency_contact_name: member.emergency_contact_name || "",
      emergency_contact_phone: member.emergency_contact_phone || "",
      blood_group: member.blood_group || "",
      medical_notes: member.medical_notes || "",
      plan: member.plan || "Monthly",
      plan_amount: member.plan_amount || 0,
      start_date: member.start_date ? new Date(member.start_date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      expiry_date: member.expiry_date ? new Date(member.expiry_date).toISOString().slice(0, 10) : "",
      waiver_signed: member.waiver_status === "signed",
      waiver_url: member.waiver_url || "",
      fitness_goal: member.fitness_goal || "",
      preferred_class: member.preferred_class || "",
    });
    setStep(0);
  }, [member, open]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Overall checklist
  const checklist = useMemo(() => STEPS.map(s => ({
    ...s,
    done: stepCompletion(form, s.key),
  })), [form]);
  const completed = checklist.filter(c => c.done).length;
  const pct = Math.round((completed / STEPS.length) * 100);

  const canSubmit = checklist.every(c => c.done);

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep(s => Math.max(0, s - 1));

  const submit = async () => {
    if (!canSubmit) {
      toast.error("Please complete required fields");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        dob: form.dob ? new Date(form.dob).toISOString() : null,
        gender: form.gender || null,
        address: form.address || null,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_phone: form.emergency_contact_phone,
        blood_group: form.blood_group || null,
        medical_notes: form.medical_notes || null,
        plan: form.plan,
        plan_amount: parseFloat(form.plan_amount) || null,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : null,
        waiver_signed: !!form.waiver_signed,
        waiver_url: form.waiver_url || null,
        fitness_goal: form.fitness_goal || null,
        preferred_class: form.preferred_class || null,
      };
      await endpoints.completeOnboarding(member.id, payload);
      toast.success("Member onboarded - automations now active", {
        description: "QR + PIN auto-generated. Retention engine is live.",
      });
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete onboarding");
    } finally { setBusy(false); }
  };

  if (!member) return null;
  const Step = STEPS[step];

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && !o && onClose()}>
      <DialogContent data-testid="onboarding-modal" className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
          <DialogTitle className="font-display flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-primary" /> Onboard {member.name}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            One-time setup · powers retention, automations, and emergency workflows.
          </p>
        </DialogHeader>

        {/* Step pills */}
        <div className="px-6 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-1.5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isCurrent = i === step;
              const isDone = checklist[i].done && i !== step;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStep(i)}
                  data-testid={`onboarding-step-pill-${s.key}`}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
                    isCurrent ? "bg-primary text-primary-foreground"
                    : isDone ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                    : "bg-card text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {isDone ? <CheckCircle2 className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  <span className="hidden sm:inline truncate">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form body */}
        <div className="px-6 py-5 overflow-y-auto" data-testid={`onboarding-step-${Step.key}`}>
          {Step.key === "basic" && (
            <div className="space-y-4">
              <Field label="Full name" required value={form.name} onChange={v => update("name", v)} testid="ob-name" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Phone number" required value={form.phone} onChange={v => update("phone", v)} testid="ob-phone" />
                <Field label="Email" type="email" value={form.email} onChange={v => update("email", v)} testid="ob-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Date of birth" required type="date" value={form.dob} onChange={v => update("dob", v)} testid="ob-dob" />
                <SelectField label="Gender" value={form.gender || "_unset"} onChange={v => update("gender", v === "_unset" ? "" : v)} testid="ob-gender" options={[
                  { value: "_unset", label: "Prefer not to say" },
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "other", label: "Other" },
                ]} />
              </div>
              <Field label="Address" value={form.address} onChange={v => update("address", v)} testid="ob-address" multiline />
            </div>
          )}

          {Step.key === "emergency" && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/30 rounded-md p-2.5">
                Combat sports / high-intensity training requires reliable emergency contact info. Required.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Emergency contact name" required value={form.emergency_contact_name} onChange={v => update("emergency_contact_name", v)} testid="ob-ec-name" />
                <Field label="Emergency contact phone" required value={form.emergency_contact_phone} onChange={v => update("emergency_contact_phone", v)} testid="ob-ec-phone" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField label="Blood group" value={form.blood_group || "_unset"} onChange={v => update("blood_group", v === "_unset" ? "" : v)} testid="ob-blood" options={[
                  { value: "_unset", label: "Unknown" },
                  ...["O+","O-","A+","A-","B+","B-","AB+","AB-"].map(g => ({ value: g, label: g })),
                ]} />
              </div>
              <Field label="Medical conditions / injuries (optional)" value={form.medical_notes} onChange={v => update("medical_notes", v)} testid="ob-medical" multiline placeholder="Asthma, knee surgery, allergies, etc." />
            </div>
          )}

          {Step.key === "membership" && (
            <div className="space-y-4">
              <SelectField label="Membership plan" required value={form.plan} onChange={v => update("plan", v)} testid="ob-plan" options={[
                { value: "Monthly",     label: "Monthly" },
                { value: "Quarterly",   label: "Quarterly" },
                { value: "Half-Yearly", label: "Half-Yearly" },
                { value: "Yearly",      label: "Yearly" },
              ]} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Start date" required type="date" value={form.start_date} onChange={v => {
                  update("start_date", v);
                  // Auto-set expiry based on plan
                  if (v && !form.expiry_date) {
                    const dur = { Monthly: 30, Quarterly: 90, "Half-Yearly": 180, Yearly: 365 }[form.plan] || 30;
                    const d = new Date(v); d.setDate(d.getDate() + dur);
                    update("expiry_date", d.toISOString().slice(0, 10));
                  }
                }} testid="ob-start" />
                <Field label="Expiry date" required type="date" value={form.expiry_date} onChange={v => update("expiry_date", v)} testid="ob-expiry" />
              </div>
              <Field label="Plan amount (₹)" type="number" value={form.plan_amount} onChange={v => update("plan_amount", v)} testid="ob-amount" />
            </div>
          )}

          {Step.key === "extras" && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border p-4 bg-muted/20">
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 mt-0.5 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">Waiver / liability</p>
                    <p className="text-xs text-muted-foreground/85 mb-3">Tick once member has signed the form (Google Form / Jotform / paper).</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!form.waiver_signed}
                        onChange={(e) => update("waiver_signed", e.target.checked)}
                        data-testid="ob-waiver-signed"
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="text-sm">Waiver signed</span>
                    </label>
                    <Input
                      type="url"
                      value={form.waiver_url}
                      onChange={(e) => update("waiver_url", e.target.value)}
                      data-testid="ob-waiver-url"
                      placeholder="https://forms.google.com/... (optional)"
                      className="mt-3"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Fitness goal (optional)" value={form.fitness_goal} onChange={v => update("fitness_goal", v)} testid="ob-goal" placeholder="Lose 5kg / Boxing fight prep / Strength" />
                <Field label="Preferred class (optional)" value={form.preferred_class} onChange={v => update("preferred_class", v)} testid="ob-pref-class" placeholder="MMA / Boxing / CrossFit / Strength" />
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer with progress + nav */}
        <DialogFooter className="px-6 py-4 border-t border-border bg-card flex-row items-center gap-3 sm:gap-3 justify-between">
          {/* Progress */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/80 mb-1">
              <span data-testid="onboarding-progress">{completed}/{STEPS.length} sections</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={prev} disabled={step === 0 || busy}
              className="px-3 py-2 text-sm rounded-md border border-border bg-card hover:bg-muted disabled:opacity-50 inline-flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Back</span>
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} disabled={busy} data-testid="onboarding-next"
                className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground font-medium hover:opacity-90 inline-flex items-center gap-1">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="onboarding-submit"
                className="px-4 py-2 text-sm rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Activate member
              </button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- helper inputs ----------
function Field({ label, value, onChange, type = "text", required, testid, multiline, placeholder }) {
  return (
    <div>
      <Label className="text-xs">
        {label}{required && <span className="text-rose-500"> *</span>}
      </Label>
      {multiline ? (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          className="min-h-[64px] text-sm"
        />
      ) : (
        <Input
          type={type}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, required, testid }) {
  return (
    <div>
      <Label className="text-xs">
        {label}{required && <span className="text-rose-500"> *</span>}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
